import { isProduction } from '../../utils/getEnvironment';

import { TOC_SCENARIOS, tocActiveRoundScenario } from './championsMock';
import type { TournamentV2Response } from './types';

const ACTIVE_ROUND_KEY = /^round(\d+)active$/;

// Case-insensitive scenario lookup so `?tocMockScenario=ChampionWon`,
// `championwon`, and `CHAMPIONWON` all resolve to the same fixture.
const SCENARIOS_BY_LOWER_KEY: Record<string, () => TournamentV2Response> = Object.fromEntries(
  Object.entries(TOC_SCENARIOS).map(([scenarioKey, scenario]) => [scenarioKey.toLowerCase(), scenario]),
);

/**
 * Dev-only: set to a scenario function for local QA without a URL param. Must stay `null` in committed code.
 */
export const TOC_MOCK_HARDCODED: (() => TournamentV2Response) | null = null;

/**
 * QA-only TOC mock switch. When active, overrides `/v2/tournament/current/{coin}` with a hard-coded fixture.
 *
 * Resolution order:
 *   1. `?tocMockScenario=<key>` URL parameter (local QA / manual testing).
 *   2. `TOC_MOCK_HARDCODED` constant (local devs flipping a scenario manually).
 *
 * Shared by `TournamentOfChampionsStore` (mock fetch payload, round finish, auto-dismiss on load).
 *
 * Available keys on `TOC_SCENARIOS` (see `./championsMock.ts`):
 * - `preStart` — tournament not yet started
 * - `round{N}Active` — active play in round N (1-indexed, up to `TOTAL_ROUNDS`)
 * - `fullBracket` — round 1 live in a full 500-player bracket, the player unranked below it (production's shape)
 * - `afterRound{N}Qualified` — won round N (1-indexed, non-final rounds only): personal experience, then PROGRESS
 * - `eliminatedRound1` — eliminated in round 1: personal experience, then nothing
 * - `eliminatedMidTournament` — eliminated mid-tournament (only when `TOTAL_ROUNDS > 2`): personal, then the Summary
 * - `championWon` — won the Finals: personal experience, then the Summary
 * - `summary` — the Finals already claimed: opens straight on the Summary
 * - `championLostFinal` — reached the final but lost: personal experience, then the Summary
 * - `claimLoopBug` — reproduces the dev claim-loop bug
 * - `customEndRound` — captured `BETWEEN_ROUNDS_ELIMINATED` payload with an unacknowledged end-of-round screen
 * - `avital` — captured `BETWEEN_ROUNDS_QUALIFIED` payload with custom round titles ("avital", "avital 2", "avital 3")
 *
 * Picking a key that doesn't exist yields `null` and every `if (resolveTocMock())` branch silently falls
 * through to the real API — so double-check the spelling.
 *
 * Active-round scenarios also accept `?tocPlayerPosition=<n>` to override the player's leaderboard
 * position as the displayed rank (1-based, 1 = rank #1). Omitted → the scenario's default
 * (round 1 defaults to rank #10).
 */
export function resolveTocMock(): (() => TournamentV2Response) | null {
  if (isProduction) {
    return null;
  }

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const rawKey = params.get('tocMockScenario');
    if (rawKey) {
      const key = rawKey.toLowerCase();
      const scenario = SCENARIOS_BY_LOWER_KEY[key];
      if (scenario) {
        const activeMatch = key.match(ACTIVE_ROUND_KEY);
        const positionParam = params.get('tocPlayerPosition');
        if (activeMatch && positionParam != null && positionParam.trim() !== '') {
          const rank = Number.parseInt(positionParam, 10);
          if (Number.isFinite(rank) && rank >= 1) {
            const roundIndex = Number.parseInt(activeMatch[1], 10) - 1;
            return () => tocActiveRoundScenario(roundIndex, rank - 1);
          }
        }
        return scenario;
      }
    }
  }
  return TOC_MOCK_HARDCODED;
}

export const TOC_MOCK_ACKNOWLEDGED: {
  dismissedRound?: number;
  eliminationComplete?: boolean;
} = {
  // dismissedRound: 0,        // user already dismissed the end-of-round screen for round 0
  // eliminationComplete: true, // user already completed the elimination flow
};
